/**
 * ToolTipCard — AutoCAD ROLLOVERTIPS parity property card.
 *
 * # What it shows
 *
 * When `ROLLOVERTIPS = 1` (default) and the cursor hovers over a single
 * entity (no command active OR mid-command per SELECTIONPREVIEW), AutoCAD
 * shows a small popup card listing the most relevant properties of the
 * entity:
 *   - Type (e.g. "Line", "Circle", "MText")
 *   - Layer
 *   - Color
 *   - Linetype
 *   - A few entity-kind-specific fields (length / radius / area / etc.)
 *
 * The card is a non-interactive overlay — clicks pass through to the
 * canvas. It hides as soon as the cursor leaves the entity (hover store
 * `entityId` becomes `null`) or when ROLLOVERTIPS is toggled off.
 *
 * # Placement
 *
 * The card is anchored at `cursorClient + (16, 16)` so it doesn't sit
 * directly under the cursor (which would visually obscure the snap
 * marker / preview highlight). When the cursor is near a viewport edge
 * the card flips horizontally / vertically to stay on-screen — the
 * `useToolTipBounds` hook reads `window.innerWidth/Height` once per
 * render and clamps the offset.
 *
 * # Why no portal
 *
 * The card mounts as a sibling of the canvas inside the editor shell.
 * jsdom-based unit tests can find it via `getByTestId` without a
 * portal-aware query helper, and the AutoCAD-style stage already has
 * a single fixed root.
 *
 * # Size budget
 *
 * Per task constraints: ≤ 200 LOC. Card is structured-data-driven —
 * each field row reads from a small `field` array so adding fields
 * costs only data, not JSX.
 */

import { coerceColor, colorToLegacyString } from "@dxf-ai-editor/drawing-model";
import type { ReactElement } from "react";
import { useMemo } from "react";

import { useT } from "../i18n";
import { getEntityById } from "../state/editorStore";
import { useStageHoverStore } from "../state/hoverState";
import { usePreviewPreferences } from "../state/previewPreferences";

const CURSOR_OFFSET_PX = 16;
const CARD_MAX_WIDTH_PX = 280;
const CARD_ESTIMATED_HEIGHT_PX = 150;

export interface ToolTipField {
  readonly label: string;
  readonly value: string;
}

/**
 * Resolve the tooltip's 5 most-useful fields for an entity. Type and
 * Layer are always shown; the rest depend on entity kind.
 *
 * Pulls from `DrawingEntity` so values are full-fidelity (not the
 * shell preview snapshot). Returns at most 6 rows so the card stays
 * visually compact.
 */
function buildFields(
  entityId: string,
  t: ReturnType<typeof useT>,
): ToolTipField[] {
  const entity = getEntityById(entityId);
  if (!entity) return [];
  const fields: ToolTipField[] = [];
  fields.push({ label: t("tooltip.type"), value: entity.type });
  fields.push({ label: t("tooltip.layer"), value: entity.layer });

  // EPIC-COLOR-UNION S1 — `entity.color` is the typed `EntityColor` union;
  // project it to the legacy display string ("ByLayer" / ACI / "#rrggbb").
  // A bare `String(...)` on the union object rendered "[object Object]" (the
  // S1 regression this fixes). `coerceColor` defends a slipped raw-string /
  // absent color (`assembleDrawingDocument` does not re-parse in PROD), and
  // `coerceColor(undefined)` → byLayer → "ByLayer", so the Color row always
  // shows (AutoCAD ROLLOVERTIPS parity).
  fields.push({
    label: t("tooltip.color"),
    value: colorToLegacyString(coerceColor((entity as { color?: unknown }).color)),
  });
  const linetype = (entity as unknown as { linetype?: string }).linetype;
  if (linetype) {
    fields.push({ label: t("tooltip.linetype"), value: linetype });
  }
  const handle = (entity as unknown as { handle?: string }).handle;
  if (handle) {
    fields.push({ label: t("tooltip.handle"), value: handle });
  }
  return fields;
}

/**
 * ToolTipCard component. Reads from the hover store + preview prefs
 * directly so the parent doesn't need to thread props through.
 *
 * Hidden when:
 *   - `rolloverTips !== 1`
 *   - `entityId` is null (no hover target)
 *   - `cursorClient` is null (cursor off-canvas)
 */
export function ToolTipCard(): ReactElement | null {
  const t = useT();
  const entityId = useStageHoverStore((s) => s.entityId);
  const cursorClient = useStageHoverStore((s) => s.cursorClient);
  const rolloverTips = usePreviewPreferences().rolloverTips;

  const fields = useMemo(() => {
    if (!entityId) return [];
    return buildFields(entityId, t);
  }, [entityId, t]);

  if (rolloverTips !== 1) return null;
  if (!entityId || !cursorClient) return null;
  if (fields.length === 0) return null;

  // Anchor near the cursor with edge-clamp so the card never spills
  // outside the viewport. We read window dimensions once per render —
  // the hover store re-emits on cursor move so this stays in sync.
  const winW = typeof window === "undefined" ? 1920 : window.innerWidth;
  const winH = typeof window === "undefined" ? 1080 : window.innerHeight;
  let left = cursorClient.x + CURSOR_OFFSET_PX;
  let top = cursorClient.y + CURSOR_OFFSET_PX;
  if (left + CARD_MAX_WIDTH_PX > winW) {
    left = Math.max(0, cursorClient.x - CURSOR_OFFSET_PX - CARD_MAX_WIDTH_PX);
  }
  if (top + CARD_ESTIMATED_HEIGHT_PX > winH) {
    top = Math.max(
      0,
      cursorClient.y - CURSOR_OFFSET_PX - CARD_ESTIMATED_HEIGHT_PX,
    );
  }

  return (
    <div
      data-testid="rollover-tooltip-card"
      role="tooltip"
      aria-live="polite"
      style={{
        position: "fixed",
        left,
        top,
        maxWidth: CARD_MAX_WIDTH_PX,
        padding: "8px 10px",
        background: "rgba(40, 40, 40, 0.92)",
        color: "#f0f0f0",
        font: "12px/1.4 -apple-system, Segoe UI, sans-serif",
        border: "1px solid #5a5a5a",
        borderRadius: 4,
        boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
        pointerEvents: "none",
        zIndex: 9000,
      }}
    >
      {fields.map((field) => (
        <div
          key={field.label}
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(60px, 35%) 1fr",
            columnGap: 8,
            margin: "1px 0",
          }}
        >
          <span style={{ color: "#a0a0a0" }}>{field.label}</span>
          <span style={{ color: "#f0f0f0", overflowWrap: "anywhere" }}>
            {field.value}
          </span>
        </div>
      ))}
    </div>
  );
}
